// Copyright 2018 Sorama B.V.

import { autoinject, BindingEngine, Disposable } from "aurelia-framework";
import { Time } from "units/Time";
import { UnitValue } from "units/Unit";
import { IPlayablePropertiesService, PlayableProperty } from "./IPlayablePropertiesService";

class InternalPlayableProperty extends PlayableProperty {
    public lastPlayTimeMs: number;
    public timer: number;
    public propertyObserver: Disposable;
}

@autoinject
export class PlayablePropertiesService implements IPlayablePropertiesService {
    private bindingEngine: BindingEngine;

    public readonly playableProperties: { [id: string]: InternalPlayableProperty } = {};

    constructor(bindingEngine: BindingEngine) {
        this.bindingEngine = bindingEngine;
    }

    public register(id: string, property: PlayableProperty): void {
        if (!this.playableProperties[id]) {
            const prop = property as InternalPlayableProperty;
            this.playableProperties[id] = prop;
            prop.propertyObserver = this.bindingEngine.propertyObserver(prop, "updateInterval").subscribe(() => {
                this.updateInterval(id);
            });
        }
    }

    public unregister(id: string): void {
        if (this.playableProperties[id]) {
            this.pause(id);
            this.playableProperties[id].propertyObserver.dispose();
            this.playableProperties[id] = undefined;
        }
    }

    public play(id: string): void {
        if (!this.playableProperties[id]) throw new Error("Requested Id not registered");

        if (this.playableProperties[id].isPlaying) return;

        // Prepare callback
        const timerCallback = (id: string) => {
            const prop = this.playableProperties[id];
            const currentTimeInMs = Date.now();
            const secondStep = new UnitValue(
                (currentTimeInMs - prop.lastPlayTimeMs) * prop.playbackRate,
                Time.milliSecond
            ).toBaseUnit();
            const keyStep = new UnitValue(secondStep * prop.playbackSpeed.value, prop.playbackSpeed.unit).toUnit(
                prop.currentKey.unit
            );

            // Update properties
            prop.currentKey = new UnitValue(prop.currentKey.value + keyStep, prop.currentKey.unit);
            prop.lastPlayTimeMs = currentTimeInMs;

            // Wrap around
            if (Math.abs(prop.maxKey.value - prop.minKey.value) < 1e-6) {
                // Equal
                prop.currentKey = new UnitValue(prop.minKey.toUnit(prop.currentKey.unit), prop.currentKey.unit);
            } else {
                while (prop.currentKey.toUnit(prop.maxKey.unit) >= prop.maxKey.value) {
                    prop.currentKey = new UnitValue(
                        prop.currentKey.value -
                            (prop.maxKey.toUnit(prop.currentKey.unit) - prop.minKey.toUnit(prop.currentKey.unit)),
                        prop.currentKey.unit
                    );
                }
            }
        };

        // Start timer
        this.playableProperties[id].lastPlayTimeMs = Date.now();
        this.playableProperties[id].isPlaying = true;
        if (!this.playableProperties[id].currentKey)
            this.playableProperties[id].currentKey = this.playableProperties[id].minKey;
        this.playableProperties[id].timer = window.setInterval(
            () => timerCallback(id),
            1000 / this.playableProperties[id].updateInterval
        );
    }

    public pause(id: string): void {
        if (!this.playableProperties[id]) throw new Error("Requested Id not registered");

        if (!this.playableProperties[id].isPlaying) return;

        window.clearInterval(this.playableProperties[id].timer);
        this.playableProperties[id].isPlaying = false;
    }

    private updateInterval(id: string) {
        // just restart the whole timer
        this.pause(id);
        this.play(id);
    }
}
